import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  FlatList,
} from 'react-native';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../../DirectionalIcon';
import { colors, spacing, typography } from '../../../theme';
import { CustomTemplate } from '../types/messageForm.types';
import { Modal } from '../../Modal';

interface TemplateListProps {
  templates: CustomTemplate[];
  isLoading: boolean;
  hasTemplates: boolean;
  onSelectTemplate: (template: CustomTemplate) => void;
  onClose: () => void;
}

export const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  isLoading,
  hasTemplates,
  onSelectTemplate,
  onClose,
}) => {
  const styles = useThemedStyles(getStyles);
  console.log('[TemplateList] Rendering with:', {
    templatesCount: templates.length,
    isLoading,
    hasTemplates,
    templates: templates.map(t => ({
      name: t.templateName,
      hasParameters: t.hasParameters,
      parametersCount: t.parameters?.length || 0,
    })),
  });

  const renderTemplateItem = (template: CustomTemplate) => (
    <TouchableOpacity
      key={template._id}
      style={styles.templateItem}
      onPress={() => onSelectTemplate(template)}
      activeOpacity={0.7}
    >
      <View style={styles.templateHeader}>
        <Text style={styles.templateName}>{template.templateName}</Text>
        <View style={[styles.categoryBadge, getCategoryStyle(styles, template.category)]}>
          <Text style={styles.categoryText}>{template.category}</Text>
        </View>
      </View>

      <Text style={styles.templateBody} numberOfLines={2}>
        {template.templateBody}
      </Text>

      <View style={styles.templateFooter}>
        <Text style={styles.languageText}>{template.language.toUpperCase()}</Text>
        <DirectionalIcon name="chevron-forward" size={20} color={colors.text.secondary} />
      </View>
    </TouchableOpacity>
  );

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading templates...</Text>
        </View>
      </View>
    );
  }

  if (!hasTemplates) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyContainer}>
          <Ionicons
            name="document-outline"
            size={64}
            color={colors.text.tertiary}
            style={styles.emptyIcon}
          />
          <Text style={styles.emptyTitle}>No Templates Available</Text>
          <Text style={styles.emptyText}>
            Custom templates will appear here once added by your administrator
          </Text>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
          >
            <Text style={styles.closeButtonText}>Close</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Select Template</Text>
        <TouchableOpacity onPress={onClose}>
          <Ionicons name="close" size={24} color={colors.text.primary} />
        </TouchableOpacity>
      </View>

      <FlatList
        data={templates}
        keyExtractor={(item) => item._id}
        renderItem={({ item }) => renderTemplateItem(item)}
        contentContainerStyle={styles.listContent}
        scrollEnabled={true}
      />
    </View>
  );
};

const getCategoryStyle = (styles: ReturnType<typeof getStyles>, category: string) => {
  switch (category) {
    case 'MARKETING':
      return styles.categoryMarketing;
    case 'TRANSACTIONAL':
      return styles.categoryTransactional;
    case 'OTP':
      return styles.categoryOtp;
    default:
      return styles.categoryOther;
  }
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
  },
  emptyIcon: {
    marginBottom: spacing.lg,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  closeButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 8,
  },
  closeButtonText: {
    color: colors.background.primary,
    fontSize: typography.fontSize.base,
    fontWeight: '600',
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  templateItem: {
    backgroundColor: colors.background.secondary,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.md,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
  },
  templateHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  templateName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
    flex: 1,
  },
  categoryBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 12,
    marginLeft: spacing.sm,
  },
  categoryMarketing: {
    backgroundColor: '#FFF3E0',
  },
  categoryTransactional: {
    backgroundColor: '#E8F5E9',
  },
  categoryOtp: {
    backgroundColor: '#F3E5F5',
  },
  categoryOther: {
    backgroundColor: '#ECEFF1',
  },
  categoryText: {
    fontSize: 10,
    fontWeight: '600',
    color: colors.text.primary,
  },
  templateBody: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginBottom: spacing.sm,
    lineHeight: 18,
  },
  templateFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
  },
  languageText: {
    fontSize: 11,
    color: colors.text.tertiary,
    fontWeight: '500',
  },
});
